// IE Hub - User Service
// Provides abstraction layer for user management API operations

import { httpGet, httpPost, httpPut, httpDelete } from '../http';
import { userEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import type {
  ApiResponse,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UserSearchParams,
  PaginatedResponse,
  RequestConfig,
  ServiceResponse,
  ServiceListResponse
} from '../types';

/**
 * User Service Class
 * Handles all user management API operations with proper error handling and monitoring
 */
export class UserService {
  /**
   * Get list of users with optional search and pagination
   */
  static async getUsers(params: UserSearchParams = {}, config?: RequestConfig): ServiceListResponse<User> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.query) queryParams.append('query', params.query);
      if (params.role) queryParams.append('role', params.role);
      if (params.status) queryParams.append('status', params.status);

      const url = `${userEndpoints.list}?${queryParams.toString()}`;
      
      const response = await httpGet<PaginatedResponse<User>>(
        url,
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_list_error', error, {
          category: 'api',
          severity: 'warning',
          context: { params },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get specific user by ID
   */
  static async getUser(userId: string | number, config?: RequestConfig): ServiceResponse<User> {
    try {
      const response = await httpGet<ApiResponse<User>>(
        userEndpoints.get(userId),
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_get_error', error, {
          category: 'api',
          severity: 'warning',
          context: { userId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Create new user
   */
  static async createUser(userData: CreateUserRequest, config?: RequestConfig): ServiceResponse<User> {
    try {
      const response = await httpPost<ApiResponse<User>>(
        userEndpoints.create,
        userData,
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_create_error', error, {
          category: 'api',
          severity: 'error',
          context: { email: userData.email, name: userData.name },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Update existing user
   */
  static async updateUser(
    userId: string | number,
    userData: UpdateUserRequest,
    config?: RequestConfig
  ): ServiceResponse<User> {
    try {
      const response = await httpPut<ApiResponse<User>>(
        userEndpoints.update(userId),
        userData,
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_update_error', error, {
          category: 'api',
          severity: 'error',
          context: { userId, updateFields: Object.keys(userData) },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Delete user
   */
  static async deleteUser(userId: string | number, config?: RequestConfig): ServiceResponse<void> {
    try {
      const response = await httpDelete<ApiResponse<void>>(
        userEndpoints.delete(userId),
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_delete_error', error, {
          category: 'api',
          severity: 'error',
          context: { userId },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Search users with query
   */
  static async searchUsers(
    query: string,
    params: Omit<UserSearchParams, 'query'> = {},
    config?: RequestConfig
  ): ServiceListResponse<User> {
    try {
      const searchParams: UserSearchParams = {
        ...params,
        query
      };

      return await this.getUsers(searchParams, config);
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_search_error', error, {
          category: 'api',
          severity: 'warning',
          context: { query, params },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Upload user avatar
   */
  static async uploadAvatar(
    userId: string | number,
    file: File,
    config?: RequestConfig
  ): ServiceResponse<{ avatarUrl: string }> {
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await httpPost<ApiResponse<{ avatarUrl: string }>>(
        userEndpoints.avatar(userId),
        formData,
        {
          timeout: config?.timeout || 30000, // Longer timeout for file uploads
          headers: {
            // Don't set Content-Type for FormData, let browser set it with boundary
            ...config?.headers
          }
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_avatar_upload_error', error, {
          category: 'api',
          severity: 'error',
          context: { 
            userId, 
            fileName: file.name, 
            fileSize: file.size,
            fileType: file.type 
          },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get users by role
   */
  static async getUsersByRole(
    role: string,
    params: Omit<UserSearchParams, 'role'> = {},
    config?: RequestConfig
  ): ServiceListResponse<User> {
    try {
      const searchParams: UserSearchParams = {
        ...params,
        role
      };

      return await this.getUsers(searchParams, config);
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_by_role_error', error, {
          category: 'api',
          severity: 'warning',
          context: { role, params },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Bulk operations helper
   */
  static async bulkUpdateUsers(
    userUpdates: Array<{ id: string | number; data: UpdateUserRequest }>,
    config?: RequestConfig
  ): Promise<Array<{ id: string | number; success: boolean; error?: string }>> {
    const results: Array<{ id: string | number; success: boolean; error?: string }> = [];

    // Process updates in parallel with concurrency limit
    const BATCH_SIZE = 5;
    for (let i = 0; i < userUpdates.length; i += BATCH_SIZE) {
      const batch = userUpdates.slice(i, i + BATCH_SIZE);
      
      const batchPromises = batch.map(async (update) => {
        try {
          await this.updateUser(update.id, update.data, { 
            ...config, 
            skipErrorCapture: true 
          });
          return { id: update.id, success: true };
        } catch (error) {
          return {
            id: update.id,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    // Log bulk operation results
    const successCount = results.filter(r => r.success).length;
    const errorCount = results.filter(r => !r.success).length;

    if (errorCount > 0 && !config?.skipErrorCapture) {
      femaCapture('user_bulk_update_errors', `Bulk update completed with ${errorCount} errors`, {
        category: 'api',
        severity: 'warning',
        context: { 
          totalUpdates: userUpdates.length,
          successCount,
          errorCount,
          errors: results.filter(r => !r.success)
        },
        skipSanitization: false,
        user: {}
      });
    }

    return results;
  }

  /**
   * Check if user exists by email
   */
  static async userExists(email: string, config?: RequestConfig): Promise<boolean> {
    try {
      const searchResult = await this.searchUsers(email, { limit: 1 }, {
        ...config,
        skipErrorCapture: true
      });

      return searchResult.data && searchResult.data.length > 0;
    } catch (error) {
      // If search fails, assume user doesn't exist
      return false;
    }
  }

  /**
   * Get user statistics
   */
  static async getUserStats(config?: RequestConfig): ServiceResponse<{
    totalUsers: number;
    activeUsers: number;
    usersByRole: Record<string, number>;
    recentRegistrations: number;
  }> {
    try {
      const response = await httpGet<ApiResponse<{
        totalUsers: number;
        activeUsers: number;
        usersByRole: Record<string, number>;
        recentRegistrations: number;
      }>>(
        `${userEndpoints.list}/stats`,
        {
          timeout: config?.timeout,
          headers: config?.headers
        }
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('user_stats_error', error, {
          category: 'api',
          severity: 'warning',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }
}

export default UserService;
